import React, { useState, useEffect } from 'react';
import OTPInput from 'otp-input-react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import useToast from '../../../util/hooks/useToast';

import Button from '../../../components/Button';
import formatSeconds from '../../../util/helpers/formatSeconds';
import ArrowRight from '../../../assets/svg_components/ArrowRight';
import { setResetOtp } from '../../../redux/slices/auth/forgotPassSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { ReactComponent as ResendCodeIcon } from '../../../assets/images/resend-code-icon.svg';
import { FORGOT_PASSWORD_BY_PHONE, FORGOT_PASSWORD_BY_EMAIL } from '../../../graphql/mutation';

import ForgotPassVStyles from './ForgotPasswordVerifyScreen.module.scss';

const ForgotPasswordVerifyScreen = () => {
  const Toast = useToast();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const forgotPassSelector = useAppSelector((state) => state.forgotPassModal);
  const { phone, email } = useAppSelector((state) => state.user);
  const [forgotPasswordByEmail] = useMutation(FORGOT_PASSWORD_BY_EMAIL);
  const [forgotPasswordByPhone] = useMutation(FORGOT_PASSWORD_BY_PHONE);

  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [time, setTime] = useState(120);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  useEffect(() => {
    if (error) {
      Toast.error(error);
    }
  }, [error, setError]);

  useEffect(() => {
    let id: any;
    if (email && !phone) {
      id = setInterval(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);
    } else if (phone && !email) {
      id = setInterval(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);
    }
    return () => {
      clearInterval(id);
      setTime(120);
    };
  }, [email, phone]);

  const handleResendEmailOtp = async () => {
    try {
      setError('');
      if (!isSendingOtp) {
        setIsSendingOtp(true);
        const resp = await forgotPasswordByEmail({
          variables: { email }
        });
        if (resp?.data?.forgotPassword?.status === 200) {
          Toast?.success(resp?.data?.forgotPassword?.message);
          setOtpVerified(true);
        }

        if (resp?.data?.forgotPassword?.status === 400) {
          setError(resp?.data?.forgotPassword?.message);
          return;
        }
        setIsSendingOtp(false);
        setTime(120);
      }
    } catch (err) {
      setIsSendingOtp(false);
      const e = err as any;
      if (e && e?.graphQLErrors && e?.graphQLErrors?.length > 0) {
        setError(e?.graphQLErrors[0]?.message);
      } else {
        setError('Something went wrong');
      }
    }
  };

  const handleResendPhoneOtp = async () => {
    try {
      setError('');
      if (!isSendingOtp) {
        setIsSendingOtp(true);
        const resp = await forgotPasswordByPhone({
          variables: { phone }
        });
        if (resp?.data?.forgotPasswordByPhone?.status === 200) {
          Toast.success(resp?.data?.forgotPasswordByPhone?.message);
        }

        if (resp?.data?.forgotPasswordByPhone?.status === 400) {
          setError(resp?.data?.forgotPasswordByPhone?.message);
          return;
        }
        setIsSendingOtp(false);
        setTime(120);
      }
    } catch (err) {
      setIsSendingOtp(false);
      const e = err as any;
      if (e && e?.graphQLErrors && e?.graphQLErrors?.length > 0) {
        setError(e?.graphQLErrors[0]?.message);
      } else {
        setError('Something went wrong');
      }
    }
  };

  const onContinue = () => {
    dispatch(setResetOtp(pin));
    navigate('/password-reset');
    setTime(0);
  };

  return (
    <div className={ForgotPassVStyles.forgotPass}>
      {!otpVerified && (
        <div className={ForgotPassVStyles.forgotPass__content}>
          <h4 className={ForgotPassVStyles.forgotPass__headText}>Request Sent Successfully</h4>

          {forgotPassSelector.userResetOption === 'EMAIL' ? (
            <p className={ForgotPassVStyles.forgotPass__subtext}>
              We have sent an email to{' '}
              <span className={ForgotPassVStyles.forgotPass__identity}>{email}</span> containing a
              verification code.
            </p>
          ) : (
            <p className={ForgotPassVStyles.forgotPass__subtext}>
              Please enter verification code we sent to{' '}
              <span className={ForgotPassVStyles.forgotPass__identity}>{phone}</span>
            </p>
          )}

          <div className={ForgotPassVStyles.forgotPass__pin}>
            <OTPInput
              className={ForgotPassVStyles.forgotPass__pinWrapper}
              inputClassName={ForgotPassVStyles.forgotPass__pinInput}
              value={pin}
              onChange={setPin}
              autoFocus={false}
              OTPLength={4}
              otpType="number"
              disabled={false}
              secure
            />
          </div>

          <div className={ForgotPassVStyles.forgotPass__instruction}>
            <div className={ForgotPassVStyles.forgotPass__notice}>
              <p className={ForgotPassVStyles.forgotPass__noticeText}>
                Having trouble finding your OTP? Check your Spam/Promotions folder. No OTP still?
                Click on &quot;Resend&quot; button.
              </p>
            </div>
          </div>

          <Button
            className={ForgotPassVStyles.forgotPass__button}
            title="Verify email address"
            isLoading={isSendingOtp}
            disabled={isSendingOtp || pin.length !== 4}
            onPress={onContinue}
          />
          <div className={ForgotPassVStyles.forgotPass__resendBeforeTimeout}>
            {time > 0 && (
              <>
                <div className={ForgotPassVStyles.forgotPass__redirect}>
                  <div className={ForgotPassVStyles.forgotPass__redirectText}>
                    <ArrowRight
                      className={ForgotPassVStyles.forgotPass__redirectArrow}
                      stroke="#5941A9"
                    />
                    <span
                      className={ForgotPassVStyles.forgotPass__redirectClickable}
                      onClick={() => {
                        navigate('/');
                      }}>
                      Go back to login
                    </span>
                  </div>
                </div>
                <p className={ForgotPassVStyles.forgotPass__beforeTimeoutText}>
                  <ResendCodeIcon />
                  Resend code in :{' '}
                  <span className={ForgotPassVStyles.forgotPass__resendTimer}>
                    {formatSeconds(time)}
                  </span>
                </p>
              </>
            )}
            {time < 0 && (
              <>
                <div className={ForgotPassVStyles.forgotPass__resend}>
                  <p className={ForgotPassVStyles.forgotPass__resendText}>
                    <span
                      className={`${ForgotPassVStyles.forgotPass__resendTimer} ${ForgotPassVStyles.forgotPass__resendClickable}`}
                      onClick={
                        forgotPassSelector.userResetOption === 'EMAIL'
                          ? handleResendEmailOtp
                          : handleResendPhoneOtp
                      }>
                      Resend code
                    </span>
                  </p>
                </div>
                <div className={ForgotPassVStyles.forgotPass__redirect}>
                  <div className={ForgotPassVStyles.forgotPass__redirectText}>
                    <ArrowRight
                      className={ForgotPassVStyles.forgotPass__redirectArrow}
                      stroke="#5941A9"
                    />
                    <span
                      className={ForgotPassVStyles.forgotPass__redirectClickable}
                      onClick={() => {
                        navigate('/');
                      }}>
                      Go back to login
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgotPasswordVerifyScreen;
